import React from 'react';
import { Button } from '@dynamon-as/ui';
import { Edit, Send, Visibility } from '@mui/icons-material';
import { Tooltip as MUITooltip } from '@mui/material';
import { type Request, RequestStatus } from '@features/requests/types';
import { useI18n } from '@shared/i18n';
import styles from './RequestActions.module.css';

/**
 * Props for RequestActions component
 */
export interface RequestActionsProps {
    /** The request to show actions for */
    request: Request;
    /** Callback when user wants to edit */
    onEdit: () => void;
    /** Callback when user wants to submit */
    onSubmit: () => void;
    /** Callback when user wants to view details */
    onView: () => void;
    /** Whether actions are disabled */
    disabled?: boolean;
}

/** Shared tooltip styling aligned with Dynamon design tokens */
const tooltipStyle = {
    backgroundColor: 'var(--dyn-color-white)',
    color: 'var(--dyn-color-black-2)',
    border: '1px solid var(--dyn-color-gray-4)',
    fontSize: 'var(--dyn-text-sm-font-size)',
    borderRadius: 'var(--dyn-radius-sm)',
    padding: 'var(--dyn-space-1) var(--dyn-space-2)',
};

interface ActionTooltipProps {
    title: string;
    children: React.ReactElement;
}

/** Wraps an action button in a themed tooltip */
function ActionTooltip({
    title,
    children,
}: ActionTooltipProps): React.ReactElement {
    return (
        <MUITooltip
            title={title}
            arrow
            slotProps={{ tooltip: { style: tooltipStyle } }}
        >
            {children}
        </MUITooltip>
    );
}

/**
 * RequestActions component
 * Displays available actions for a request based on its status.
 */

/**
 * Displays available actions for a request based on its status.
 * All actions are icon buttons; the primary action (Send inn)
 * is rendered last.
 */
export function RequestActionsComponent({
    request,
    onEdit,
    onSubmit,
    onView,
    disabled = false,
}: RequestActionsProps): React.ReactElement {
    const { t } = useI18n();
    const isDraft = request.status === RequestStatus.DRAFT;
    const isDelivered = request.status === RequestStatus.SUBMITTED;
    const isDeclined = request.status === RequestStatus.REJECTED;
    const isTerminal = !isDraft && !isDelivered && !isDeclined;
    const hasDates = request.startDate && request.endDate;

    // Prevent clicks from bubbling to the clickable list row
    const stop =
        (handler: () => void) =>
        (event: React.MouseEvent<HTMLButtonElement>): void => {
            event.stopPropagation();
            handler();
        };

    if (isTerminal) {
        return (
            <ActionTooltip title={t('requests.actions.viewDetails')}>
                <Button
                    Icon={Visibility}
                    color="primary"
                    variant="flat"
                    rounded={true}
                    size="small"
                    onClick={stop(onView)}
                    disabled={disabled}
                />
            </ActionTooltip>
        );
    }

    return (
        <div className={styles.group}>
            {(isDraft || isDeclined) && (
                <ActionTooltip title={t('requests.actions.edit')}>
                    <Button
                        Icon={Edit}
                        color="secondary"
                        variant="flat"
                        rounded={true}
                        size="small"
                        onClick={stop(onEdit)}
                        disabled={disabled}
                    />
                </ActionTooltip>
            )}
            <ActionTooltip title={t('requests.actions.viewDetails')}>
                <Button
                    Icon={Visibility}
                    color="primary"
                    variant="flat"
                    rounded={true}
                    size="small"
                    onClick={stop(onView)}
                    disabled={disabled}
                />
            </ActionTooltip>
            {(isDraft || isDeclined) && (
                <ActionTooltip
                    title={
                        !hasDates
                            ? t('requests.actions.submitDisabledNoDates')
                            : isDeclined
                              ? t('requests.actions.resubmit')
                              : t('requests.actions.submit')
                    }
                >
                    <Button
                        Icon={Send}
                        color="success"
                        variant="flat"
                        rounded={true}
                        size="small"
                        onClick={stop(onSubmit)}
                        disabled={disabled || !hasDates}
                    />
                </ActionTooltip>
            )}
        </div>
    );
}
